/**
 * WeatherOverviewTab
 *
 * This component provides a comprehensive overview of current weather conditions for a selected tower.
 *
 * Key Features:
 * - Real-time weather metrics displayed in circular progress meters
 * - Critical weather parameters monitoring:
 *   - Wind Speed (with risk level indicators)
 *   - Temperature
 *   - Humidity
 *   - Precipitation (with risk level indicators)
 * - Active weather alerts for dangerous conditions
 * - Detailed weather information table
 * - Tower selection dropdown for quick switching between towers
 *
 * Risk Levels:
 * - Wind Speed: High (>50 km/h), Medium (>30 km/h), Low (<30 km/h)
 * - Precipitation: High (>5 mm), Medium (>2 mm), Low (<2 mm)
 * - Lightning: Any detection is considered High risk
 *
 * Use Cases:
 * - Quick assessment of current weather conditions
 * - Monitoring critical weather parameters for maintenance planning
 * - Real-time risk assessment for field operations
 * - Multi-tower weather comparison
 */

"use client"

import type React from "react"
import { useState, useRef } from "react"
import { View, StyleSheet, ScrollView, Platform } from "react-native"
import { Text, useTheme, Card, Button, Menu, Divider, Portal } from "react-native-paper"
import { CircularProgressbar, buildStyles } from "react-circular-progressbar"
import "react-circular-progressbar/dist/styles.css"
import { Wind, Thermometer, Droplets, CloudRain, Zap, ChevronDown } from "lucide-react"

interface WeatherOverviewTabProps {
  weatherData: any
  selectedTower: any
  towers: any[]
  onTowerChange: (tower: any) => void
}

export const WeatherOverviewTab: React.FC<WeatherOverviewTabProps> = ({
  weatherData,
  selectedTower,
  towers,
  onTowerChange,
}) => {
  const theme = useTheme()
  const [towerMenuVisible, setTowerMenuVisible] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const buttonRef = useRef<View | null>(null)
  const [buttonPosition, setButtonPosition] = useState({ x: 0, y: 0, width: 0, height: 0 })

  // Function to measure button position for proper menu placement
  const measureButton = () => {
    if (buttonRef.current && Platform.OS === "web") {
      const node = buttonRef.current as unknown as HTMLElement
      const rect = node.getBoundingClientRect()
      setButtonPosition({
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height,
      })
    }
  }

  // Handle opening the dropdown
  const openDropdown = () => {
    measureButton()
    setDropdownOpen(true)
  }

  if (!weatherData || !selectedTower) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <Text>No weather data available. Please select a tower.</Text>
      </View>
    )
  }

  const { values } = weatherData.data
  const windSpeed = values.windSpeed
  const temperature = values.temperature
  const humidity = values.humidity
  const precipitation = values.precipitationIntensity
  const lightning = values.lightningStrikeCount

  // Determine risk levels
  const windRisk = windSpeed > 50 ? "high" : windSpeed > 30 ? "medium" : "low"
  const lightningRisk = lightning > 0 ? "high" : "low"
  const precipitationRisk = precipitation > 5 ? "high" : precipitation > 2 ? "medium" : "low"

  // Get risk color
  const getRiskColor = (risk: string) => {
    switch (risk) {
      case "high":
        return "#ff4d4f"
      case "medium":
        return "#faad14"
      case "low":
        return "#52c41a"
      default:
        return "#52c41a"
    }
  }

  return (
    <ScrollView style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={styles.header}>
        <Text variant="headlineMedium">Weather Overview</Text>

        {/* Tower Selection - Web Implementation */}
        {Platform.OS === "web" && (
          <View ref={buttonRef}>
            <Button
              mode="outlined"
              onPress={openDropdown}
              icon={({ size, color }) => <ChevronDown size={size} color={color} />}
              contentStyle={styles.dropdownButton}
            >
              {selectedTower ? `Tower: ${selectedTower.name}` : "Select Tower"}
            </Button>

            {dropdownOpen && (
              <Portal>
                <View
                  style={[
                    styles.customDropdown,
                    {
                      position: "absolute",
                      top: buttonPosition.y + buttonPosition.height,
                      left: buttonPosition.x,
                      width: buttonPosition.width,
                      backgroundColor: theme.colors.surface,
                      borderColor: theme.colors.outline,
                      zIndex: 1000,
                      maxHeight: 250, // Set maximum height
                    },
                  ]}
                >
                  <ScrollView style={styles.dropdownScroll}>
                    {towers.map((tower) => (
                      <Button
                        key={tower.id}
                        mode="text"
                        onPress={() => {
                          onTowerChange(tower)
                          setDropdownOpen(false)
                        }}
                        style={styles.dropdownItem}
                      >
                        {tower.name}
                      </Button>
                    ))}
                  </ScrollView>
                </View>
                <View style={styles.dropdownBackdrop} onTouchStart={() => setDropdownOpen(false)} />
              </Portal>
            )}
          </View>
        )}

        {/* Tower Selection - Native Implementation */}
        {Platform.OS !== "web" && (
          <Menu
            visible={towerMenuVisible}
            onDismiss={() => setTowerMenuVisible(false)}
            anchor={
              <Button mode="outlined" onPress={() => setTowerMenuVisible(true)}>
                {selectedTower ? `Tower: ${selectedTower.name}` : "Select Tower"}
              </Button>
            }
          >
            {towers.map((tower) => (
              <Menu.Item
                key={tower.id}
                onPress={() => {
                  onTowerChange(tower)
                  setTowerMenuVisible(false)
                }}
                title={tower.name}
              />
            ))}
          </Menu>
        )}
      </View>

      {lightning > 0 && (
        <Card style={[styles.alertCard, { backgroundColor: "#fff2f0", borderColor: "#ffccc7" }]}>
          <Card.Content style={styles.alertContent}>
            <Zap size={24} color="#f5222d" />
            <Text variant="titleMedium" style={{ color: "#f5222d", marginLeft: 8 }}>
              Lightning Alert! {lightning} strikes detected nearby. Exercise caution.
            </Text>
          </Card.Content>
        </Card>
      )}

      {windSpeed > 50 && (
        <Card style={[styles.alertCard, { backgroundColor: "#fff2f0", borderColor: "#ffccc7" }]}>
          <Card.Content style={styles.alertContent}>
            <Wind size={24} color="#f5222d" />
            <Text variant="titleMedium" style={{ color: "#f5222d", marginLeft: 8 }}>
              High Wind Alert! Wind speed exceeds safety limits ({windSpeed} km/h).
            </Text>
          </Card.Content>
        </Card>
      )}

      <View style={styles.metersContainer}>
        {/* Wind Speed Meter */}
        <View style={styles.meterCard}>
          <Text variant="titleMedium" style={styles.meterTitle}>
            Wind Speed
          </Text>
          <View style={styles.meterWrapper}>
            <CircularProgressbar
              value={windSpeed}
              maxValue={100}
              text={`${windSpeed} km/h`}
              styles={buildStyles({
                textSize: "16px",
                pathColor: getRiskColor(windRisk),
                textColor: "#333",
                trailColor: "#d9d9d9",
              })}
            />
          </View>
          <View style={[styles.riskIndicator, { backgroundColor: getRiskColor(windRisk) }]}>
            <Text style={styles.riskText}>{windRisk.toUpperCase()} RISK</Text>
          </View>
          <View style={styles.meterIcon}>
            <Wind size={24} color={getRiskColor(windRisk)} />
          </View>
        </View>

        {/* Temperature Meter */}
        <View style={styles.meterCard}>
          <Text variant="titleMedium" style={styles.meterTitle}>
            Temperature
          </Text>
          <View style={styles.meterWrapper}>
            <CircularProgressbar
              value={temperature}
              maxValue={50}
              text={`${temperature}°C`}
              styles={buildStyles({
                textSize: "16px",
                pathColor: "#f5222d",
                textColor: "#333",
                trailColor: "#d9d9d9",
              })}
            />
          </View>
          <View style={styles.meterIcon}>
            <Thermometer size={24} color="#f5222d" />
          </View>
        </View>

        {/* Humidity Meter */}
        <View style={styles.meterCard}>
          <Text variant="titleMedium" style={styles.meterTitle}>
            Humidity
          </Text>
          <View style={styles.meterWrapper}>
            <CircularProgressbar
              value={humidity}
              maxValue={100}
              text={`${humidity}%`}
              styles={buildStyles({
                textSize: "16px",
                pathColor: "#1890ff",
                textColor: "#333",
                trailColor: "#d9d9d9",
              })}
            />
          </View>
          <View style={styles.meterIcon}>
            <Droplets size={24} color="#1890ff" />
          </View>
        </View>

        {/* Precipitation Meter */}
        <View style={styles.meterCard}>
          <Text variant="titleMedium" style={styles.meterTitle}>
            Precipitation
          </Text>
          <View style={styles.meterWrapper}>
            <CircularProgressbar
              value={precipitation * 10}
              maxValue={100}
              text={`${precipitation} mm`}
              styles={buildStyles({
                textSize: "16px",
                pathColor: getRiskColor(precipitationRisk),
                textColor: "#333",
                trailColor: "#d9d9d9",
              })}
            />
          </View>
          <View style={[styles.riskIndicator, { backgroundColor: getRiskColor(precipitationRisk) }]}>
            <Text style={styles.riskText}>{precipitationRisk.toUpperCase()} RISK</Text>
          </View>
          <View style={styles.meterIcon}>
            <CloudRain size={24} color={getRiskColor(precipitationRisk)} />
          </View>
        </View>
      </View>

      <Card style={styles.detailsCard}>
        <Card.Title title="Weather Details" />
        <Card.Content>
          <View style={styles.detailsTable}>
            <View style={styles.detailsRow}>
              <Text style={styles.detailsLabel}>Tower ID:</Text>
              <Text style={styles.detailsValue}>{selectedTower.name}</Text>
            </View>
            <Divider style={styles.divider} />
            <View style={styles.detailsRow}>
              <Text style={styles.detailsLabel}>Location:</Text>
              <Text style={styles.detailsValue}>
                {selectedTower.lat.toFixed(6)}, {selectedTower.lng.toFixed(6)}
              </Text>
            </View>
            <Divider style={styles.divider} />
            <View style={styles.detailsRow}>
              <Text style={styles.detailsLabel}>Wind Speed:</Text>
              <Text style={styles.detailsValue}>{windSpeed} km/h</Text>
            </View>
            <Divider style={styles.divider} />
            <View style={styles.detailsRow}>
              <Text style={styles.detailsLabel}>Temperature:</Text>
              <Text style={styles.detailsValue}>{temperature}°C</Text>
            </View>
            <Divider style={styles.divider} />
            <View style={styles.detailsRow}>
              <Text style={styles.detailsLabel}>Humidity:</Text>
              <Text style={styles.detailsValue}>{humidity}%</Text>
            </View>
            <Divider style={styles.divider} />
            <View style={styles.detailsRow}>
              <Text style={styles.detailsLabel}>Precipitation:</Text>
              <Text style={styles.detailsValue}>{precipitation} mm</Text>
            </View>
            <Divider style={styles.divider} />
            <View style={styles.detailsRow}>
              <Text style={styles.detailsLabel}>Lightning Strikes:</Text>
              <Text style={styles.detailsValue}>{lightning}</Text>
            </View>
            <Divider style={styles.divider} />
            <View style={styles.detailsRow}>
              <Text style={styles.detailsLabel}>Overall Risk:</Text>
              <Text
                style={[
                  styles.detailsValue,
                  {
                    color:
                      windRisk === "high" || lightningRisk === "high" || precipitationRisk === "high"
                        ? "#f5222d"
                        : windRisk === "medium" || precipitationRisk === "medium"
                          ? "#faad14"
                          : "#52c41a",
                  },
                ]}
              >
                {windRisk === "high" || lightningRisk === "high" || precipitationRisk === "high"
                  ? "HIGH"
                  : windRisk === "medium" || precipitationRisk === "medium"
                    ? "MEDIUM"
                    : "LOW"}
              </Text>
            </View>
          </View>
        </Card.Content>
      </Card>
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  alertCard: {
    marginBottom: 16,
    borderWidth: 1,
  },
  alertContent: {
    flexDirection: "row",
    alignItems: "center",
  },
  metersContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  meterCard: {
    width: "48%",
    backgroundColor: "white",
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    position: "relative",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  meterTitle: {
    textAlign: "center",
    marginBottom: 16,
  },
  meterWrapper: {
    width: "100%",
    height: 150,
  },
  meterIcon: {
    position: "absolute",
    top: 16,
    right: 16,
  },
  riskIndicator: {
    marginTop: 16,
    padding: 4,
    borderRadius: 4,
    alignItems: "center",
  },
  riskText: {
    color: "white",
    fontWeight: "bold",
    fontSize: 12,
  },
  detailsCard: {
    marginBottom: 16,
  },
  detailsTable: {
    width: "100%",
  },
  detailsRow: {
    flexDirection: "row",
    paddingVertical: 8,
  },
  detailsLabel: {
    width: "30%",
    fontWeight: "bold",
  },
  detailsValue: {
    width: "70%",
  },
  divider: {
    marginVertical: 4,
  },
  // Custom dropdown styles
  dropdownButton: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  customDropdown: {
    borderWidth: 1,
    borderRadius: 4,
    overflow: "hidden",
    elevation: 4,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  dropdownItem: {
    justifyContent: "flex-start",
    borderRadius: 0,
  },
  dropdownBackdrop: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "transparent",
    zIndex: 999,
  },
  dropdownScroll: {
    width: "100%",
  },
})

